import { EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber } from 'ApplicationEntry/models/EnteredPolicyNumber';
import { EmptyString, isNonEmptyString, NonEmptyString, StringUnion } from 'Core/models/EmptyString';
import * as I from 'immutable';
import { ToPartialMatchOptions } from 'Shared/utilities/MapDiscriminatedUnion';

type CriticalIllnessProps = {
  planType: StringUnion;
  benefitAmount: number;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  insuredSC: StringUnion;
  spouseSC: StringUnion;
  section125: boolean;
  replacement: boolean;
  policyNumber: PolicyNumber;
  riderCodeList: I.List<string>;
  riderAmounts: I.List<number>;
  cancerBenefit: boolean;
};

export class InvalidNewYorkCriticalIllness {
  public readonly key = 'InvalidNewYorkCriticalIllness';

  constructor(
    public readonly planType: StringUnion,
    public readonly benefitAmount: number,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly insuredSC: StringUnion,
    public readonly spouseSC: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly riderCodeList: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly cancerBenefit: boolean
  ) { }
}

export class ValidNewYorkCriticalIllness {
  public static createApp({
    planType,
    benefitAmount,
    premium,
    calculatedPremium,
    cwa,
    insuredSC,
    spouseSC,
    section125,
    replacement,
    policyNumber,
    riderCodeList,
    riderAmounts,
    cancerBenefit,
  }: CriticalIllnessProps) {
    return this.create(
      planType,
      benefitAmount,
      premium,
      calculatedPremium,
      cwa,
      insuredSC,
      spouseSC,
      section125,
      replacement,
      policyNumber,
      riderCodeList,
      riderAmounts,
      cancerBenefit
    );
  }

  public static create(
    planType: StringUnion,
    benefitAmount: number,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    insuredSC: StringUnion,
    spouseSC: StringUnion,
    section125: boolean,
    replacement: boolean,
    policyNumber: PolicyNumber,
    riderCodeList: I.List<string>,
    riderAmounts: I.List<number>,
    cancerBenefit: boolean
  ) {
    return isNonEmptyString(planType)
      ? new ValidNewYorkCriticalIllness(
        planType,
        benefitAmount,
        premium,
        calculatedPremium,
        cwa,
        insuredSC,
        spouseSC,
        section125,
        replacement,
        policyNumber,
        riderCodeList,
        riderAmounts,
        cancerBenefit
      )
      : new InvalidNewYorkCriticalIllness(
        planType,
        benefitAmount,
        premium,
        calculatedPremium,
        cwa,
        insuredSC,
        spouseSC,
        section125,
        replacement,
        policyNumber,
        riderCodeList,
        riderAmounts,
        cancerBenefit
      );
  }

  public readonly key = 'ValidNewYorkCriticalIllness';
  constructor(
    public readonly planType: StringUnion,
    public readonly benefitAmount: number,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly insuredSC: StringUnion,
    public readonly spouseSC: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly riderCodeList: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly cancerBenefit: boolean
  ) { }
}

export type NewYorkCriticalIllness = InvalidNewYorkCriticalIllness | ValidNewYorkCriticalIllness;

export type NewYorkCriticalIllnessOptions<TReturn> = ToPartialMatchOptions<NewYorkCriticalIllness, 'key', TReturn>;

export const matchNewYorkCriticalIllness = <TReturn>(options: NewYorkCriticalIllnessOptions<TReturn>) => (
  app: NewYorkCriticalIllness
): TReturn => {
  switch (app.key) {
    case 'InvalidNewYorkCriticalIllness':
      return options.InvalidNewYorkCriticalIllness ? options.InvalidNewYorkCriticalIllness(app) : options.default(app);
    case 'ValidNewYorkCriticalIllness':
      return options.ValidNewYorkCriticalIllness ? options.ValidNewYorkCriticalIllness(app) : options.default(app);
  }
};

export const EmptyNewYorkCriticalIllness = new InvalidNewYorkCriticalIllness(
  new EmptyString(),
  0,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  EmptyPolicyNumber,
  I.List<string>(),
  I.List<number>(),
  false
);

export const changePlanType = (planType: string) => (app: NewYorkCriticalIllness) =>
  ValidNewYorkCriticalIllness.createApp({ ...app, planType: NonEmptyString.create(planType) });

export const changeBenefitAmount = (benefitAmount: number) => (app: NewYorkCriticalIllness) =>
  ValidNewYorkCriticalIllness.createApp({ ...app, benefitAmount });

export const changePremium = (premium: string) => (app: NewYorkCriticalIllness) =>
  ValidNewYorkCriticalIllness.createApp({ ...app, premium: NonEmptyString.create(premium) });

export const changeCalculatedPremium = (calculatedPremium: string) => (app: NewYorkCriticalIllness) =>
  ValidNewYorkCriticalIllness.createApp({ ...app, calculatedPremium: NonEmptyString.create(calculatedPremium) });

export const changeCWA = (cwa: string) => (app: NewYorkCriticalIllness) =>
  ValidNewYorkCriticalIllness.createApp({ ...app, cwa: NonEmptyString.create(cwa) });

export const changeInsuredSC = (insuredSC: string) => (app: NewYorkCriticalIllness) =>
  ValidNewYorkCriticalIllness.createApp({ ...app, insuredSC: NonEmptyString.create(insuredSC) });

export const changeSpouseSC = (spouseSC: string) => (app: NewYorkCriticalIllness) =>
  ValidNewYorkCriticalIllness.createApp({ ...app, spouseSC: NonEmptyString.create(spouseSC) });

export const changeSection125 = (section125: boolean) => (app: NewYorkCriticalIllness) =>
  ValidNewYorkCriticalIllness.createApp({ ...app, section125 });

export const changeReplacement = (replacement: boolean) => (app: NewYorkCriticalIllness) =>
  ValidNewYorkCriticalIllness.createApp({
    ...app,
    replacement,
    policyNumber: ValidPolicyNumber.create(new EmptyString()),
  });

export const changePolicyNumber = (policyNumber: StringUnion) => (app: NewYorkCriticalIllness) =>
  ValidNewYorkCriticalIllness.createApp({ ...app, policyNumber: ValidPolicyNumber.create(policyNumber) });

export const changeRiderCodes = (riderCode: string, index: number) => (app: NewYorkCriticalIllness) => {
  const existingList: I.List<string> = app.riderCodeList;
  const newList = existingList.set(index, riderCode);
  return ValidNewYorkCriticalIllness.createApp({ ...app, riderCodeList: newList });
};

export const changeRiderAmount = (riderAmt: number, index: number) => (app: NewYorkCriticalIllness) => {
  const existingList: I.List<number> = app.riderAmounts;
  const newList = existingList.set(index, riderAmt);
  return ValidNewYorkCriticalIllness.createApp({ ...app, riderAmounts: newList });
};

export const removeRider = (index: number) => (app: NewYorkCriticalIllness) => {
  const existingListAmt: I.List<number> = app.riderAmounts;
  const newListAmt = existingListAmt.delete(index);
  const existingList: I.List<string> = app.riderCodeList;
  const newList = existingList.delete(index);
  return ValidNewYorkCriticalIllness.createApp({ ...app, riderAmounts: newListAmt, riderCodeList: newList });
};

export const changeCancerBenefit = (benefit: boolean) => (app: NewYorkCriticalIllness) =>
  ValidNewYorkCriticalIllness.createApp({ ...app, cancerBenefit: benefit });

export const riderCodes = [
  {
    code: 'WBR4NY',
    units: [1, 2, 3, 4],
  },
];
export const getAllRiderCodeUnits = () => {
  return riderCodes;
};

export const getRiderUnits = (riderCode: string | undefined) => {
  if (riderCode !== undefined && riderCode !== 'None' && riderCode !== '') {
    const index = riderCodes.findIndex((rc: { code: string }) => rc.code === riderCode);
    return riderCodes[index].units;
  }
  return [];
};
